<template>
  <div class="group-prices-container">
    <div class="group-prices-header">
      <div class="header-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      </div>
      <h1>Csoport árak</h1>
      <p>Óránkénti és havi alapárak beállítása csoportonként</p>
    </div>

    <div class="group-prices-content">
      <div class="section">
        <div class="section-header">
          <h2>Csoport dijszabás</h2>
          <button @click="saveAllGroupPrices" class="btn-primary" :disabled="savingGroupPrices">
            {{ savingGroupPrices ? 'Mentés...' : 'Összes mentése' }}
          </button>
        </div>
        <p class="section-hint">Állítsd be az egyes csoportok óránkénti és havi alapárát (HUF). Ezek az árak szolgálnak alapul a számlázásnál.</p>
        <div v-if="groupPriceRows.length > 0" class="group-price-table-wrap">
          <table class="group-price-table">
            <thead>
              <tr>
                <th>Csoport</th>
                <th>Óra ára (Ft)</th>
                <th>Havi ár (Ft)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in groupPriceRows" :key="row.group_id">
                <td class="group-name">{{ row.name }}</td>
                <td>
                  <input type="number" step="0.01" min="0" v-model.number="row.per_class" placeholder="0" />
                </td>
                <td>
                  <input type="number" step="0.01" min="0" v-model.number="row.monthly" placeholder="0" />
                </td>
                <td>
                  <button class="btn-primary btn-sm" @click="saveGroupPrice(row)" :disabled="savingGroupPrices">
                    {{ row.saving ? 'Mentés...' : 'Mentés' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="no-payments">
          <p>Nincsenek elérhető csoportok az árazáshoz.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import toastMixin from '../mixins/toast.js';

export default {
  mixins: [toastMixin],
  data() {
    return {
      groupPriceRows: [],
      savingGroupPrices: false
    };
  },
  created() {
    this.loadGroupPrices();
  },
  methods: {
    async loadGroupPrices() {
      try {
        const [groupsRes, pricesRes] = await Promise.all([
          axios.get(this.$BACKEND_HOST + '/api/settings/groups/info'),
          axios.get(this.$BACKEND_HOST + '/api/billing/group-prices')
        ]);
        const groups = groupsRes.data.groups || [];
        const prices = {};
        (pricesRes.data.prices || []).forEach(p => { prices[p.group_id] = p; });
        this.groupPriceRows = groups.map(g => ({
          group_id: g.id,
          name: g.name,
          per_class: prices[g.id] ? (prices[g.id].price_per_class || 0) : 0,
          monthly: prices[g.id] ? (prices[g.id].price_monthly || 0) : 0,
          saving: false
        }));
      } catch (error) {
        console.error('Error loading group prices:', error);
      }
    },
    async saveGroupPrice(row) {
      if (!row || !row.group_id) return;
      row.saving = true;
      this.savingGroupPrices = true;
      try {
        await axios.put(this.$BACKEND_HOST + '/api/billing/group-prices', {
          group_id: parseInt(row.group_id),
          price_per_class: parseFloat(row.per_class) || 0,
          price_monthly: parseFloat(row.monthly) || 0
        });
        await this.loadGroupPrices();
        this.showToast('success', 'Csoport ár mentve');
      } catch (error) {
        console.error('Error saving group price:', error);
        this.showToast('error', 'Hiba a csoport ár mentésekor: ' + (error.response?.data?.message || error.message));
      }
      row.saving = false;
      this.savingGroupPrices = false;
    },
    async saveAllGroupPrices() {
      if (this.groupPriceRows.length === 0) {
        this.showToast('error', 'Nincsenek menthető csoportok');
        return;
      }
      this.savingGroupPrices = true;
      try {
        await Promise.all(this.groupPriceRows.map(row => axios.put(this.$BACKEND_HOST + '/api/billing/group-prices', {
          group_id: parseInt(row.group_id),
          price_per_class: parseFloat(row.per_class) || 0,
          price_monthly: parseFloat(row.monthly) || 0
        })));
        await this.loadGroupPrices();
        this.showToast('success', 'Minden csoport ár mentve');
      } catch (error) {
        console.error('Error saving all group prices:', error);
        this.showToast('error', 'Hiba a csoport árak mentésekor: ' + (error.response?.data?.message || error.message));
      }
      this.savingGroupPrices = false;
    }
  }
};
</script>

<style scoped>
.group-prices-container {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.group-prices-header {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  color: white;
  text-align: center;
  box-shadow: var(--shadow-lg);
  margin-bottom: 2rem;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  margin-bottom: 0.5rem;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.group-prices-header h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.group-prices-header p {
  font-size: 0.95rem;
  opacity: 0.9;
  margin: 0;
}

.section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  margin-bottom: 2rem;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.section-header h2 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.section-hint {
  padding: 0 2rem 1rem;
  color: var(--color-text-muted);
  font-size: 0.9rem;
  margin: 0;
}

.group-price-table-wrap {
  padding: 1rem 2rem 2rem;
  overflow-x: auto;
}

.group-price-table {
  width: 100%;
  border-collapse: collapse;
}

.group-price-table th,
.group-price-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-border);
  text-align: left;
}

.group-price-table th {
  color: var(--color-heading);
  font-weight: 600;
}

.group-price-table input {
  width: 160px;
  padding: 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text);
}

.group-name {
  font-weight: 600;
  color: var(--color-heading);
}

.btn-sm {
  padding: 0.5rem 0.9rem;
  font-size: 0.9rem;
}

.no-payments {
  text-align: center;
  padding: 3rem;
  color: var(--color-text-muted);
}

.btn-primary {
  background: var(--primary-blue);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  background: var(--secondary-blue);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .group-prices-container {
    padding: 1rem 0.5rem;
  }

  .group-price-table-wrap {
    padding: 0.75rem 0.5rem;
  }

  .group-price-table th,
  .group-price-table td {
    padding: 0.5rem 0.4rem;
    font-size: 0.85rem;
    white-space: nowrap;
  }

  .group-price-table input {
    width: 90px;
    padding: 0.4rem;
    font-size: 0.85rem;
  }

  .section-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
    padding: 1rem 1.25rem;
  }

  .section-hint {
    padding: 0.5rem 1rem 0.75rem;
  }
}
</style>
